import { ControlledIndexTable, ControlledIndexTableProps, YStack } from '@shinetools/lumen-react'
import { ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SuppliersTableTitle } from './elements/SuppliersTableTitle'
import { SuppliersTableColumn } from './types/suppliersTableColumn'
import { SuppliersTableHideableColumnKey } from './types/suppliersTableHideableColumnKey'
import { SuppliersTableRow } from './types/suppliersTableRow'
import { SuppliersTableSortCriterion } from './types/suppliersTableSortCriterion'
import { isSuppliersTableHideableColumnKey } from './utils/isSuppliersTableHideableColumnKey'
import { isSuppliersTableSortCriterion } from './utils/isSuppliersTableSortCriterion'

type ExtendedControlledIndexTableProps = Omit<
  ControlledIndexTableProps<SuppliersTableRow>,
  | 'columns'
  | 'loading'
  | 'onFilterClick'
  | 'onQueryChange'
  | 'onSortCriteriaChange'
  | 'pageSizeOptions'
  | 'query'
  | 'searchable'
  | 'sortCriteria'
>

export interface SuppliersTableProps extends ExtendedControlledIndexTableProps {
  columns: SuppliersTableColumn[]
  isLoading?: boolean
  onSearchChange: (searchValue: string) => void
  onSortCriteriaChange?: (sortCriteria: SuppliersTableSortCriterion | null) => void
  pageSizeOptions: readonly number[]
  searchValue: string
  sortCriteria?: SuppliersTableSortCriterion | null
}

export const SuppliersTable = ({
  columns,
  isLoading,
  onSearchChange,
  onSortCriteriaChange,
  pageSizeOptions,
  searchValue,
  sortCriteria,
  ...rest
}: SuppliersTableProps): ReactElement => {
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<Set<SuppliersTableHideableColumnKey>>(new Set())
  const { t } = useTranslation()

  const visibleColumns = useMemo<SuppliersTableColumn[]>(
    () =>
      columns
        .filter((column) => {
          if (isSuppliersTableHideableColumnKey(column.key)) {
            return !hiddenColumnKeys.has(column.key)
          }

          return true
        })
        .map((column) => (typeof column.header === 'string' ? { ...column, header: t(column.header) } : column)),
    [columns, hiddenColumnKeys, t],
  )

  const formattedPageSizeOptions = pageSizeOptions?.map<
    NonNullable<ControlledIndexTableProps['pageSizeOptions']>[number]
  >((size) => ({
    label: size.toString(),
    value: size.toString(),
  }))

  return (
    <YStack gap={12}>
      <SuppliersTableTitle
        columns={columns}
        hiddenColumnKeys={hiddenColumnKeys}
        onSearchChange={onSearchChange}
        searchValue={searchValue}
        setHiddenColumnKeys={setHiddenColumnKeys}
      />

      <ControlledIndexTable
        {...rest}
        columns={visibleColumns}
        loading={isLoading}
        onQueryChange={() => null}
        /**
         * ControlledIndexTable supports multi-criteria sort by default with no opt-out.
         * We only support single-criteria sort, thus we only pass the last sort criterion.
         */
        onSortCriteriaChange={(nextSortCriteria) => {
          const sortCriterion = nextSortCriteria.at(-1)
          onSortCriteriaChange?.(isSuppliersTableSortCriterion(sortCriterion) ? sortCriterion : null)
        }}
        pageSizeOptions={formattedPageSizeOptions}
        query=""
        sortCriteria={sortCriteria ? [sortCriteria] : []}
      />
    </YStack>
  )
}
